import { ref } from 'vue';
import type { PitchDetectionResult } from '../index';

/**
 * Metadata for a recorded pitch detection session
 */
export interface RecordingMetadata {
  sessionId: string;
  timestamp: string;
  userAgent: string;
  exerciseType: string;
  exerciseConfig: any;
  description?: string;
  expectedOutcome?: 'success' | 'failure' | 'contested';
}

/**
 * Complete recording session with metadata and frames
 */
export interface PitchRecordingSession {
  metadata: RecordingMetadata;
  frames: PitchDetectionResult[];
  performance?: {
    completed: boolean;
    wrongAttempts?: number;
    duration?: number;
    [key: string]: any;
  };
}

/**
 * Composable for recording pitch detection sessions
 *
 * Usage:
 * ```typescript
 * const recorder = usePitchRecorder();
 *
 * // Start recording
 * recorder.start({
 *   exerciseType: 'sequence-matcher',
 *   exerciseConfig: { sequence: ['C4', 'D4', 'E4'] }
 * });
 *
 * // In your pitch detection loop
 * const result = detector.detectPitch(...);
 * recorder.capture(result);
 *
 * // Download when done
 * recorder.download('my-test-case.json');
 * ```
 */
export function usePitchRecorder() {
  const frames = ref<PitchDetectionResult[]>([]);
  const isRecording = ref(false);
  const metadata = ref<RecordingMetadata | null>(null);
  const startTime = ref<number>(0);

  /**
   * Start recording a new session
   */
  function start(config: {
    exerciseType: string;
    exerciseConfig: any;
    description?: string;
    expectedOutcome?: 'success' | 'failure' | 'contested';
  }) {
    const sessionId = generateSessionId();
    const timestamp = new Date().toISOString();
    const userAgent = navigator.userAgent;

    metadata.value = {
      sessionId,
      timestamp,
      userAgent,
      exerciseType: config.exerciseType,
      exerciseConfig: config.exerciseConfig,
      description: config.description,
      expectedOutcome: config.expectedOutcome,
    };

    frames.value = [];
    isRecording.value = true;
    startTime.value = Date.now();

    console.log(`[PitchRecorder] Started recording session ${sessionId}`);
  }

  /**
   * Capture a pitch detection frame
   * Call this in your pitch detection loop when isRecording is true
   */
  function capture(result: PitchDetectionResult) {
    if (!isRecording.value) return;

    frames.value.push({
      ...result,
    });
  }

  /**
   * Stop recording
   */
  function stop() {
    if (!isRecording.value) return;

    isRecording.value = false;
    const duration = Date.now() - startTime.value;

    console.log(
      `[PitchRecorder] Stopped recording. Captured ${frames.value.length} frames over ${duration}ms`
    );
  }

  /**
   * Download the recording as JSON
   */
  function download(filename?: string, performanceData?: any) {
    if (!metadata.value) {
      console.warn('[PitchRecorder] No metadata available. Did you call start()?');
      return;
    }

    const session: PitchRecordingSession = {
      metadata: metadata.value,
      frames: frames.value,
      performance: performanceData,
    };

    const json = JSON.stringify(session, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const defaultFilename = `${metadata.value.exerciseType}-${metadata.value.sessionId.substring(0, 8)}.json`;
    const finalFilename = filename || defaultFilename;

    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    link.click();

    URL.revokeObjectURL(url);

    console.log(`[PitchRecorder] Downloaded recording as ${finalFilename}`);
  }

  /**
   * Clear the current recording without downloading
   */
  function clear() {
    frames.value = [];
    metadata.value = null;
    isRecording.value = false;
    console.log('[PitchRecorder] Cleared recording');
  }

  /**
   * Get the current session data (for preview or analysis)
   */
  function getSession(performanceData?: any): PitchRecordingSession | null {
    if (!metadata.value) return null;

    return {
      metadata: metadata.value,
      frames: frames.value,
      performance: performanceData,
    };
  }

  return {
    // State
    frames,
    isRecording,
    metadata,
    frameCount: ref(frames.value.length),

    // Methods
    start,
    capture,
    stop,
    download,
    clear,
    getSession,
  };
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Replay a recorded session through a callback
 * Useful for testing
 */
export function replayRecording(
  session: PitchRecordingSession,
  onFrame: (result: PitchDetectionResult, index: number) => void
) {
  session.frames.forEach((frame, index) => {
    onFrame(frame, index);
  });
}
